import { useCallback, useMemo, useState } from 'react';
import type { CompanyTaskMessage } from '@gokrew/models/tasks';
import type {
  TaskMessageBubbleEditing,
  TaskMessageBubbleLabels,
} from '@/components/tasks/TaskMessageBubble';

interface UseTaskMessageEditingOptions {
  threadId: string | undefined;
  updateMessage: (
    args: { threadId: string; messageId: string; body: string },
    options: { onSuccess: () => void }
  ) => void;
  deleteMessage: (args: { threadId: string; messageId: string }) => void;
  editedLabel: string;
  saveLabel: string;
  cancelLabel: string;
}

/**
 * @example
 * const messageBubbleProps = useTaskMessageEditing({
 *   threadId,
 *   updateMessage: updateMsg, // or a thin adapter if the mutation needs a nested payload
 *   deleteMessage: deleteMsg,
 *   editedLabel: t('...edited'),
 *   saveLabel: t('...save'),
 *   cancelLabel: t('...cancel'),
 * });
 * <TaskMessageBubble message={msg} isOwn={...} accent="indigo" language={...} {...messageBubbleProps} />
 */
export function useTaskMessageEditing({
  threadId,
  updateMessage,
  deleteMessage,
  editedLabel,
  saveLabel,
  cancelLabel,
}: UseTaskMessageEditingOptions) {
  const [editingMessageId, setEditingMessageId] = useState<string | null>(
    null
  );
  const [text, setText] = useState('');

  const handleStart = useCallback((message: CompanyTaskMessage) => {
    setEditingMessageId(message.id);
    setText(message.body);
  }, []);

  const handleSave = useCallback(
    (message: CompanyTaskMessage, value: string) => {
      if (!value.trim() || !threadId) return;
      updateMessage(
        { threadId, messageId: message.id, body: value.trim() },
        { onSuccess: () => setEditingMessageId(null) }
      );
    },
    [threadId, updateMessage]
  );

  const handleCancel = useCallback(() => setEditingMessageId(null), []);

  const handleDelete = useCallback(
    (message: CompanyTaskMessage) => {
      if (!threadId) return;
      deleteMessage({ threadId, messageId: message.id });
    },
    [threadId, deleteMessage]
  );

  const labels: TaskMessageBubbleLabels = useMemo(
    () => ({ edited: editedLabel, save: saveLabel, cancel: cancelLabel }),
    [editedLabel, saveLabel, cancelLabel]
  );

  const editing: TaskMessageBubbleEditing = useMemo(
    () => ({
      editingMessageId,
      text,
      onTextChange: setText,
      onStart: handleStart,
      onSave: handleSave,
      onCancel: handleCancel,
    }),
    [editingMessageId, text, handleStart, handleSave, handleCancel]
  );

  return { labels, editing, onDelete: handleDelete };
}
